import React, { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import styles from "./GenerateReportModal.module.scss";
import { UserContext } from "components/UserGuard";
import Modal from "components/shared/Modal";
import { ModalProps } from "store/types/ComponentProps";
import DateFormItem from "components/shared/DateFormItem";
import { GenerateReportFormValues } from "store/types/FormValues";
import { defaultFormProps, getRequiredValidationRule, getValidationPropsFromError } from "util/Form";
import moment from "moment";
import { Program } from "store/types/User";
import { CheckBox } from "@mui/icons-material";

interface GenerateReportModalProps extends ModalProps {
  onSubmit: (generateReportValues: GenerateReportFormValues) => void;
  title: string;
  subTitle: string;
  contentTypeId: number;
  showDates: boolean;
  selectMultiple: boolean;
  showHelperText: boolean;
  showFailuresFlag: boolean;
  formats: string[];
}

const defaultValues: GenerateReportFormValues = {
  programId: "",
  startDate: null,
  endDate: null,
  format: "csv",
  showFailures: false,
};

const GenerateReportModal: React.FunctionComponent<GenerateReportModalProps> = ({
  title,
  subTitle,
  contentTypeId,
  open,
  onSubmit,
  onClose,
  loading,
  showDates = true,
  selectMultiple = false,
  showHelperText = false,
  showFailuresFlag = false,
  formats,
}) => {
  const { programs } = useContext(UserContext);
  const [selectedPrograms, setSelectedPrograms] = useState<[]>([]);

  const handleProgramSelection = (event: SelectChangeEvent<[]>) => {
    const current = event.target.value as [];
    const newList: [] = [];
    for (let i = 0; i < current.length; i++) {
      const index = getIndexOfProgram(newList, current[i][0]);
      if (index >= 0) {
        newList.splice(index, 1);
      } else {
        newList.push(current[i]);
      }
    }
    setSelectedPrograms(newList);
  };

  const determineProgramList = (program: Program): boolean => {
    if (contentTypeId == 9 || contentTypeId == 13 || contentTypeId == 14 || contentTypeId == 0) {
      return program.programType == "Training Program" && program.programDesignation == "Registered";
    } else if (
      contentTypeId == 20 ||
      contentTypeId == 21 ||
      contentTypeId == 22 ||
      contentTypeId == 23 ||
      contentTypeId == 24
    ) {
      return program.programType == "Assessment Program" && program.programDesignation == "Registered";
    } else {
      return false;
    }
  };

  const methods = useForm<GenerateReportFormValues>({
    ...defaultFormProps,
    defaultValues,
  });
  const { control, formState, handleSubmit, reset, watch } = methods;

  const startDateValue = watch("startDate");
  const endDateValue = watch("endDate");

  const submitButtonDisabled: boolean = useMemo(
    () =>
      !formState.isValid ||
      !formState.isDirty ||
      loading ||
      (showDates && !startDateValue) ||
      (showDates && !endDateValue) ||
      (selectMultiple && selectedPrograms?.length == 0),
    [formState.isValid, formState.isDirty, loading, startDateValue, endDateValue, selectMultiple, selectedPrograms]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [title, contentTypeId, reset]);

  const handleUpdateMethod = useCallback(
    (formValues: GenerateReportFormValues) => {
      if (selectMultiple) {
        const arr: string[] = [];
        selectedPrograms.forEach((element) => {
          arr.push(element[0]);
        });
        formValues.programId = arr.join(",");
        setSelectedPrograms([]);
      }
      onSubmit(formValues);
    },
    [onSubmit, selectedPrograms]
  );

  const handleFormSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      handleSubmit((data: GenerateReportFormValues) => handleUpdateMethod(data))();
    },
    [handleSubmit, handleUpdateMethod]
  );

  const closeModal = useCallback(() => {
    setSelectedPrograms([]);
    onClose();
  }, []);

  const getIndexOfProgram = (list: [], id: string) => {
    for (let i = 0; i < list.length; i++) if (list[i][0] == id) return i;
    return -1;
  };

  return (
    <Modal
      maxWidth={"md"}
      loading={loading}
      open={open}
      title={title}
      subTitleText={subTitle}
      headerBodyDivider={true}
      onClose={closeModal}
      actions={
        <>
          <Button color={"primary"} variant={"outlined"} onClick={closeModal}>
            Cancel
          </Button>
          <Button
            color={"primary"}
            variant={"contained"}
            type={"submit"}
            onClick={handleFormSubmit}
            disabled={submitButtonDisabled}
          >
            {contentTypeId == 0 ? "Generate File" : "Generate Report"}
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant={"h6"}>
              {contentTypeId == 0
                ? selectMultiple
                  ? "Programs"
                  : "Program"
                : selectMultiple
                ? "Locations"
                : "Location"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {selectMultiple ? (
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    {...getValidationPropsFromError(error)}
                    inputRef={field.ref}
                    placeholder={"Search for organizations or programs"}
                    fullWidth={true}
                    displayEmpty={true}
                    value={selectedPrograms}
                    onChange={handleProgramSelection}
                    multiple={selectMultiple}
                    renderValue={(selected) =>
                      selected
                        .map(function (elem) {
                          return elem[1];
                        })
                        .join(", ")
                    }
                  >
                    <MenuItem value={""} disabled>
                      {"Search for organizations or programs"}
                    </MenuItem>
                    {programs
                      ?.filter((p) => determineProgramList(p))
                      .sort((a, b) => a.programName.localeCompare(b.programName))
                      .map((value) => (
                        <MenuItem key={value.programId} value={[value.programId, value.programName]}>
                          <Checkbox checked={getIndexOfProgram(selectedPrograms, value.programId) > -1} />
                          <ListItemText primary={value.programName} />
                        </MenuItem>
                      ))}
                  </Select>
                )}
                name={"programId"}
                control={control}
              />
            ) : (
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    {...getValidationPropsFromError(error)}
                    inputRef={field.ref}
                    placeholder={"Search for organizations or programs"}
                    fullWidth={true}
                    displayEmpty={true}
                  >
                    <MenuItem value={""} disabled>
                      {"Search for organizations or programs"}
                    </MenuItem>
                    {programs
                      ?.filter((p) => determineProgramList(p))
                      .sort((a, b) => a.programName.localeCompare(b.programName))
                      .map((value) => (
                        <MenuItem key={value.programId} value={value.programId}>
                          {value.programName}
                        </MenuItem>
                      ))}
                  </Select>
                )}
                name={"programId"}
                control={control}
                rules={{ required: getRequiredValidationRule("program", true, true) }}
              />
            )}
          </Grid>
          {showDates && (
            <>
              <Grid item xs={12}>
                <Typography variant={"h6"} display="inline">
                  {"Date Range "}
                </Typography>
                <Typography variant={"body2"} display="inline">
                  {"(select/enter up to 12 month date range)"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <DateFormItem
                  disabled={false}
                  fieldName={"startDate"}
                  minDate={endDateValue ? moment(endDateValue).subtract(12, "months") : undefined}
                  maxDate={endDateValue ? moment(endDateValue) : moment(new Date()).startOf("day")}
                  required={false}
                  className={styles.inputField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateFormItem
                  disabled={false}
                  fieldName={"endDate"}
                  minDate={startDateValue ? moment(startDateValue) : undefined}
                  maxDate={startDateValue ? moment(startDateValue).add(12, "months") : undefined}
                  required={false}
                  className={styles.inputField}
                />
              </Grid>
            </>
          )}
          {formats?.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant={"h6"}>File Format</Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      {...getValidationPropsFromError(error)}
                      inputRef={field.ref}
                      fullWidth={true}
                      displayEmpty={false}
                    >
                      {formats.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value == "csv" ? value.toUpperCase() + " (Excel)" : value.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  name={"format"}
                  control={control}
                />
              </Grid>
            </>
          )}
          {showFailuresFlag && (
            <>
              <Grid item xs={12}>
                <Controller
                  render={({ field }) => (
                    <FormControlLabel
                      label={"Include Failures?"}
                      control={<Checkbox {...field} checked={field.value} color={"primary"} />}
                      // style={{ float: "right" }}
                    />
                  )}
                  name={"showFailures"}
                  control={control}
                />
              </Grid>
            </>
          )}
          {showHelperText && (
            <Grid item xs={12}>
              <Typography paragraph={true} sx={{ marginBottom: "0px" }}>
                {"Once you select "}
                <strong>{"Generate Report"}</strong>
                {" you will need to go to the "}
                <strong>{"Retrieve File"}</strong>
                {
                  " tab to view your report. Depending on the size of the your report, please allow up to 24 hours for your report to generate."
                }
              </Typography>
            </Grid>
          )}
        </Grid>
      </FormProvider>
    </Modal>
  );
};
export default GenerateReportModal;
